import { ApiError } from "@/api/fetch";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useState } from "react";
import { CostLegend } from "../components/CostLegend";
import { CostPerRunTable } from "../components/CostPerRunTable";
import { RunDetailDialog } from "../components/RunDetailDialog";
import { RunsSection } from "../components/RunsSection";
import { StepTable } from "../components/StepTable";
import { UsageTotals } from "../components/UsageTotals";
import { useUsageSummary } from "../hooks/useUsageTracking";

const RANGES = [
  { days: 1, label: "Last 24 hours" },
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
];

const SECTION_TITLE = "text-lg font-semibold text-zinc-900";
const SECTION_TEXT = "mt-1 max-w-3xl text-sm text-zinc-600";

/** The cost and the time of the runs of all users. */
export const UsageTrackingPage = () => {
  useDocumentTitle("Usage tracking");
  const [days, setDays] = useState(30);
  const [openRun, setOpenRun] = useState<{ id: string | null; open: boolean }>({
    id: null,
    open: false,
  });
  const summary = useUsageSummary(days);
  const noAccess = summary.error instanceof ApiError && summary.error.status === 404;

  return (
    <div className="h-full overflow-y-auto bg-white">
      <div className="mx-auto max-w-6xl space-y-10 px-8 py-8">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl leading-none tracking-tight text-zinc-900">
              Usage tracking
            </h1>
            <p className="mt-3 max-w-3xl text-base leading-7 text-zinc-600">
              The cost and the time of each run, for all users. A run is one chat turn with the
              work that it starts, such as maps, checks, and Jev. A CLI command that does not come
              from a chat is a run of its own.
            </p>
          </div>
          <div>
            <label htmlFor="usage-range" className="mb-1 block text-xs font-medium text-zinc-600">
              Period
            </label>
            <select
              id="usage-range"
              className="h-9 rounded-[3px] border border-zinc-200 bg-white px-2 text-sm text-zinc-900"
              value={days}
              onChange={(event) => setDays(Number(event.target.value))}
            >
              {RANGES.map((range) => (
                <option key={range.days} value={range.days}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>
        </header>

        {summary.isLoading ? (
          <p className="text-sm text-zinc-500" aria-live="polite">
            Loading the usage…
          </p>
        ) : noAccess ? (
          <p role="alert" className="text-sm text-zinc-600">
            Usage tracking is only for GXL team members.
          </p>
        ) : summary.isError || !summary.data ? (
          <p role="alert" className="text-sm text-zinc-600">
            Could not load the usage. Reload the page to try again.
          </p>
        ) : (
          <>
            <UsageTotals summary={summary.data} />
            <section aria-labelledby="usage-cost-per-run" className="space-y-3">
              <div>
                <h2 id="usage-cost-per-run" className={SECTION_TITLE}>
                  Cost per run
                </h2>
                <p className={SECTION_TEXT}>
                  Agent time is the time until the chat agent stops. Total time also includes the
                  work that continues after it, such as a map that runs in the background.
                </p>
              </div>
              <CostPerRunTable groups={summary.data.groups} />
            </section>
            <section aria-labelledby="usage-cost-per-step" className="space-y-3">
              <div>
                <h2 id="usage-cost-per-step" className={SECTION_TITLE}>
                  Cost per step
                </h2>
                <p className={SECTION_TEXT}>
                  The model calls of all runs, by the step that made them. Input tokens include
                  the cached input tokens.
                </p>
              </div>
              <StepTable steps={summary.data.steps} />
            </section>
            <section aria-labelledby="usage-cost-legend" className="space-y-3">
              <h2 id="usage-cost-legend" className={SECTION_TITLE}>
                What the costs include
              </h2>
              <CostLegend summary={summary.data} />
            </section>
          </>
        )}

        {!noAccess && (
          <section aria-labelledby="usage-runs" className="space-y-3">
            <div>
              <h2 id="usage-runs" className={SECTION_TITLE}>
                Runs
              </h2>
              <p className={SECTION_TEXT}>
                Open a run to see the cost and the tokens of each step and model.
              </p>
            </div>
            <RunsSection days={days} onOpenRun={(id) => setOpenRun({ id, open: true })} />
          </section>
        )}
      </div>
      <RunDetailDialog
        runId={openRun.id}
        open={openRun.open}
        onClose={() => setOpenRun((run) => ({ ...run, open: false }))}
      />
    </div>
  );
};
